import { memo, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Bdg, Av, Counter, Ring, BarChart, AiScoreRing, Sparkline, PH } from '../components/shared';
import {
  getDashboardStats,
  getDashboardTopPerformers,
  getDashboardLeadSources,
  getDashboardWeeklyPerformance,
  getDashboardLeadQueue,
  getDashboardTopRegions,
  getDashboardTapPerformance,
  getDashboardTrafficSources,
  getDashboardFunnel,
  getDashboardCampaignPerformance,
  getDashboardMonthlyGoals,
  getDashboardNfcStatus,
  getDashboardRecentLeads,
  getDashboardActivityHeatmap,
} from '../services/api';

/* ── helpers ── */
const ini = (n) => { if(!n) return '??'; const p=n.trim().split(/\s+/); return (p[0]?.[0]||'')+(p[1]?.[0]||''); };
const greet = () => { const h=new Date().getHours(); return h<12?'Good morning':h<17?'Good afternoon':'Good evening'; };

/* ── color palettes ── */
const REGION_COL = ['#0891b2','#7c3aed','#d97706','#059669','#dc2626','#64748b'];
const TAP_COL = ['#18181b','#0891b2','#d97706','#7c3aed','#0369a1'];
const TRAFFIC_COL = ['#18181b','#0891b2','#7c3aed','#d97706','#059669'];
const FUNNEL_COL = ['#0891b2','#64748b','#d97706','#7c3aed','#059669'];

/* ── sparkline seed data (used until API provides trends) ── */
const SPARKS = {
  members:[38,40,41,39,43,45,48],
  leads:[980,1050,1100,1090,1180,1280,1347],
  taps:[3200,3400,3600,3800,4100,4300,4572],
  score:[74,76,75,78,79,81,82],
};

/* ── glass card base for KPI cards ── */
const kpiCard = {
  background:"rgba(255,255,255,.72)",border:"1px solid rgba(255,255,255,.9)",borderRadius:18,
  boxShadow:"0 4px 24px rgba(0,0,0,.06),inset 0 1px 0 rgba(255,255,255,.95)",
};

/* ════════════════════════════════════════════════════
   PAGE DASHBOARD
════════════════════════════════════════════════════ */
export const PageDashboard = memo(() => {
  const navigate = useNavigate();
  const { user } = useApp();

  /* ── Quick Actions dropdown ── */
  const [qOpen, setQOpen] = useState(false);
  const qRef = useRef(null);
  useEffect(()=>{
    if(!qOpen) return;
    const handler = (e) => { if(qRef.current && !qRef.current.contains(e.target)) setQOpen(false); };
    document.addEventListener("mousedown", handler);
    return ()=>document.removeEventListener("mousedown", handler);
  },[qOpen]);

  const QUICK = [
    {ico:"📡",lbl:"Create Campaign",path:"/campaigns/email",col:"#0891b2"},
    {ico:"➕",lbl:"Invite Member",path:"/team/users/new",col:"#059669"},
    {ico:"⬇",lbl:"Export Leads",path:"/leads",col:"#7c3aed"},
    {ico:"✦",lbl:"AI Intelligence",path:"/ai",col:"#d97706"},
    {ico:"📊",lbl:"Analytics",path:"/leads/insights",col:"#dc2626"},
  ];

  /* ── API state ── */
  const [stats, setStats] = useState(null);
  const [performers, setPerformers] = useState(null);
  const [leadSources, setLeadSources] = useState(null);
  const [weeklyPerf, setWeeklyPerf] = useState(null);
  const [leadQueue, setLeadQueue] = useState(null);
  const [regions, setRegions] = useState(null);
  const [tapPerf, setTapPerf] = useState(null);
  const [traffic, setTraffic] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [campPerf, setCampPerf] = useState(null);
  const [goals, setGoals] = useState(null);
  const [nfcStatus, setNfcStatus] = useState(null);
  const [recentLeads, setRecentLeads] = useState(null);
  const [heatmap, setHeatmap] = useState(null);

  const load = useCallback(async () => {
    const r = await Promise.allSettled([
      getDashboardStats(),           // 0
      getDashboardTopPerformers(),   // 1
      getDashboardLeadSources(),     // 2
      getDashboardWeeklyPerformance(),// 3
      getDashboardLeadQueue(),       // 4
      getDashboardTopRegions(),      // 5
      getDashboardTapPerformance(),  // 6
      getDashboardTrafficSources(),  // 7
      getDashboardFunnel(),          // 8
      getDashboardCampaignPerformance(), // 9
      getDashboardMonthlyGoals(),    // 10
      getDashboardNfcStatus(),       // 11
      getDashboardRecentLeads(),     // 12
      getDashboardActivityHeatmap(), // 13
    ]);
    const v = (i) => r[i].status==='fulfilled' ? r[i].value : null;
    setStats(v(0));
    setPerformers(v(1)?.data || []);
    setLeadSources(v(2));
    setWeeklyPerf(v(3)?.data || []);
    setLeadQueue(v(4)?.data || []);
    setRegions(v(5)?.data || []);
    setTapPerf(v(6));
    setTraffic(v(7));
    setFunnel(v(8)?.data || []);
    setCampPerf(v(9));
    setGoals(v(10));
    setNfcStatus(v(11));
    setRecentLeads(v(12)?.data || []);
    setHeatmap(v(13));
  }, []);

  useEffect(() => { load(); }, [load]);

  const userName = user?.name || 'there';
  const today = new Date().toLocaleDateString('en-US',{year:'numeric',month:'long',day:'numeric'});

  return (
    <div className="pg fi">
      {/* ── HEADER ── */}
      <PH eyebrow="Overview" title={`${greet()}, ${userName.split(' ')[0]} ✦`}
        sub={`${today} · ${stats?.totalMembers ?? '–'} active members`} live
      />

      {/* ══════════ ROW 1: KPI STAT CARDS WITH SPARKLINES ══════════ */}
      <div className="g4" style={{marginBottom:20}}>
        {[
          {lbl:"Total Members",val:stats?.totalMembers??0,ico:"👥",col:"#64748b",bg:"rgba(100,116,139,.1)",sh:"rgba(100,116,139,.2)",ch:`${stats?.totalMembers??0} members`,up:true,spark:SPARKS.members},
          {lbl:"Leads Captured",val:stats?.totalLeads??0,ico:"🎯",col:"#059669",bg:"rgba(5,150,105,.1)",sh:"rgba(5,150,105,.2)",ch:`${(stats?.leadsChange??0)>=0?'↑':'↓'} ${Math.abs(stats?.leadsChange??0)}%`,up:(stats?.leadsChange??0)>=0,spark:SPARKS.leads},
          {lbl:"Total Taps",val:stats?.totalTaps??0,ico:"💳",col:"#0f172a",bg:"rgba(15,23,42,.08)",sh:"rgba(15,23,42,.16)",ch:`${(stats?.totalViews??0).toLocaleString()} views`,up:true,spark:SPARKS.taps},
          {lbl:"AI Score Avg",val:stats?.avgAiScore??0,ico:"✦",col:"#d97706",bg:"rgba(217,119,6,.1)",sh:"rgba(217,119,6,.2)",ch:`${stats?.thisWeekLeads??0} leads this week`,up:true,spark:SPARKS.score},
        ].map(({lbl,val,ico,col,bg,sh,ch,up,spark},i)=>(
          <div key={lbl} className={`fi s${i+1}`} style={{...kpiCard,padding:"18px 20px",position:"relative",overflow:"hidden",minWidth:0}}>
            <div style={{position:"absolute",top:-20,right:-20,width:90,height:90,borderRadius:"50%",background:bg,opacity:.6,pointerEvents:"none"}}/>
            <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:10}}>
              <div style={{width:36,height:36,borderRadius:10,background:bg,boxShadow:`0 4px 14px ${sh}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16}}>{ico}</div>
              <Sparkline data={spark} color={col} height={38} width={80}/>
            </div>
            <div style={{fontSize:11,color:"var(--t3)",marginBottom:5,fontWeight:540}}>{lbl}</div>
            <div style={{fontFamily:"var(--fd)",fontSize:28,color:col,letterSpacing:"-1px",lineHeight:1}}><Counter to={val}/></div>
            <div style={{fontSize:11,color:up?"#059669":"#dc2626",marginTop:6,fontWeight:600,display:"flex",alignItems:"center",gap:4}}>
              <span>{ch}</span>
            </div>
          </div>
        ))}
      </div>

      {/* ══════════ ROW 2: LEAD SOURCES + TOP PERFORMERS ══════════ */}
      <div className="g2" style={{marginBottom:20}}>
        <div className="card fi s1">
          <div className="ch"><div className="ct">Lead Sources</div></div>
          {leadSources?.data?.length > 0 ? (
            <>
              {leadSources.data.map(({label,pct,count})=>{
                const colors={'NFC Tap':'#64748b','Form Submission':'#059669','Manual':'#18181b','User to User':'#d97706'};
                const col=colors[label]||'#64748b';
                return (
                  <div key={label} className="lr" style={{flexDirection:"column",alignItems:"stretch",gap:5}}>
                    <div style={{display:"flex",justifyContent:"space-between",fontSize:12}}>
                      <span style={{display:"flex",alignItems:"center",gap:6,color:"var(--t2)"}}>
                        <span style={{width:7,height:7,borderRadius:"50%",background:col,display:"inline-block"}}/>{label}
                      </span>
                      <b style={{color:"var(--t1)"}}>{pct}%</b>
                    </div>
                    <div className="pb"><div className="pb-f" style={{width:`${pct}%`,background:col}}/></div>
                  </div>
                );
              })}
              <div style={{display:"flex",justifyContent:"center",padding:"8px 0 14px"}}><Ring pct={leadSources.data[0]?.pct??0}/></div>
            </>
          ) : <div className="lr" style={{justifyContent:'center',color:'var(--t3)',fontSize:12}}>No leads yet</div>}
        </div>
        <div className="card fi s2">
          <div className="ch">
            <div><div className="ct">Top Performers</div><div className="cs">AI-ranked by impact</div></div>
            <div className="ca" onClick={()=>navigate('/leads/board')}>Leaderboard</div>
          </div>
          {performers && performers.length > 0 ? performers.slice(0,4).map((m,i)=>(
            <div key={m.id} className="lr">
              <div style={{position:"relative",flexShrink:0}}>
                {m.photo
                  ? <img src={m.photo} alt="" style={{width:32,height:32,borderRadius:'50%',objectFit:'cover'}}/>
                  : <Av name={ini(m.name)} grad="linear-gradient(150deg,#64748b,#059669)" size={32}/>
                }
                {i<3&&(
                  <div style={{position:"absolute",top:-5,left:-5,width:16,height:16,borderRadius:"50%",
                    background:["linear-gradient(135deg,#d97706,#fbbf24)","linear-gradient(135deg,#94a3b8,#cbd5e1)","linear-gradient(135deg,#b45309,#d97706)"][i],
                    display:"flex",alignItems:"center",justifyContent:"center",fontSize:7,fontWeight:900,color:"#fff",
                    boxShadow:"0 1px 4px rgba(0,0,0,.2)"}}>
                    {["🥇","🥈","🥉"][i]}
                  </div>
                )}
              </div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:12.5,fontWeight:560,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{m.name}</div>
                <div style={{fontSize:10.5,color:"var(--t3)"}}>{m.lead_count} leads · {m.taps??0} taps</div>
              </div>
              <AiScoreRing score={m.avg_ai_score??0}/>
            </div>
          )) : (
            <div className="lr" style={{justifyContent:'center',color:'var(--t3)',fontSize:12}}>No data yet</div>
          )}
        </div>
      </div>

      {/* ══════════ ROW 3: WEEKLY PERF / AI QUEUE ══════════ */}
      <div className="g2e" style={{marginBottom:20}}>
        {/* Weekly Performance */}
        <div className="card fi s2">
          <div className="ch"><div><div className="ct">Weekly Performance</div><div className="cs">Leads · Conversions</div></div></div>
          <div className="cb">
            <BarChart data={weeklyPerf}/>
            <div style={{display:"flex",gap:12,marginTop:11}}>
              {[["#64748b","Leads"],["#059669","Conv."]].map(([c,l])=>(
                <div key={l} style={{display:"flex",alignItems:"center",gap:5,fontSize:11,color:"var(--t3)"}}><div style={{width:9,height:9,borderRadius:3,background:c}}/>{l}</div>
              ))}
            </div>
          </div>
        </div>

        {/* AI Lead Queue */}
        <div className="card fi s3">
          <div className="ch"><div><div className="ct">AI Lead Queue</div><div className="cs">Top priority today</div></div><span className="ai-chip">✦ Auto-scored</span></div>
          {leadQueue && leadQueue.length>0 ? leadQueue.map(l=>(
            <div key={l.id} className="lr">
              <AiScoreRing score={l.score??0}/>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:12.5,fontWeight:560,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{l.name||'Unknown'}</div>
                <div style={{fontSize:11,color:"var(--t3)",overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{l.company||'—'}</div>
              </div>
              <Bdg s={l.status||'new'}/>
            </div>
          )) : <div className="lr" style={{justifyContent:'center',color:'var(--t3)',fontSize:12}}>No scored leads</div>}
        </div>
      </div>

      {/* ══════════ ROW 4: REGIONS / TAP PERF / TRAFFIC ══════════ */}
      <div className="g3" style={{marginBottom:20}}>
        {/* Top Regions */}
        <div className="card fi s2">
          <div className="ch">
            <div><div className="ct">Top Regions</div><div className="cs">Leads by location</div></div>
            <span style={{fontSize:10,fontWeight:640,color:"var(--t3)",background:"rgba(100,116,139,.1)",padding:"3px 8px",borderRadius:20}}>🌍 Global</span>
          </div>
          {regions&&regions.length>0 ? regions.map((r,i)=>{
            const col=REGION_COL[i%REGION_COL.length];
            return (
              <div key={r.city+i} style={{display:"flex",alignItems:"center",gap:10,padding:"7px 20px",borderBottom:i<regions.length-1?"1px solid rgba(0,0,0,.04)":"none"}}>
                <div style={{width:18,fontSize:10.5,fontWeight:700,color:i<3?col:"var(--t4)",textAlign:"center",flexShrink:0}}>{i+1}</div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:12.5,fontWeight:570,color:"var(--t1)",marginBottom:3,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{r.city}{r.country?`, ${r.country}`:''}</div>
                  <div className="pb" style={{height:4}}><div className="pb-f" style={{width:`${r.pct}%`,background:col,borderRadius:2}}/></div>
                </div>
                <div style={{fontSize:12.5,fontWeight:680,color:col,flexShrink:0,minWidth:28,textAlign:"right"}}>{r.leads}</div>
              </div>
            );
          }) : <div style={{padding:20,textAlign:'center',color:'var(--t3)',fontSize:12}}>No location data</div>}
        </div>

        {/* Tap Performance */}
        <div className="card fi s1">
          <div className="ch">
            <div><div className="ct">Device Tap Performance</div><div className="cs">NFC taps · by profile</div></div>
            <span style={{fontSize:10,fontWeight:640,color:"#0891b2",background:"rgba(8,145,178,.1)",padding:"3px 8px",borderRadius:20}}>💳 Live</span>
          </div>
          <div style={{textAlign:"center",padding:"10px 0 14px"}}>
            <div style={{fontSize:44,fontWeight:800,color:"var(--t1)",letterSpacing:"-2px",lineHeight:1}}>{(tapPerf?.totalTaps??0).toLocaleString()}</div>
            <div style={{fontSize:12,color:"var(--t3)",marginTop:4}}>Total NFC Taps</div>
            {tapPerf?.tapChange!=null&&(
              <div style={{display:"inline-flex",alignItems:"center",gap:5,marginTop:8,padding:"4px 10px",background:tapPerf.tapChange>=0?"rgba(5,150,105,.08)":"rgba(220,38,38,.08)",borderRadius:20}}>
                <span style={{fontSize:10,fontWeight:700,color:tapPerf.tapChange>=0?"#059669":"#dc2626"}}>{tapPerf.tapChange>=0?'↑':'↓'} {Math.abs(tapPerf.tapChange)}% vs last month</span>
              </div>
            )}
          </div>
          {tapPerf?.profiles?.length>0 ? tapPerf.profiles.map((p,i)=>{
            const col=TAP_COL[i%TAP_COL.length];
            return (
              <div key={p.id} style={{display:"flex",alignItems:"center",gap:9,padding:"5px 0"}}>
                <div style={{width:10,height:10,borderRadius:3,background:col,flexShrink:0,boxShadow:`0 0 6px ${col}66`}}/>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:11.5,fontWeight:550,color:"var(--t2)",marginBottom:3,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{p.name}</div>
                  <div className="pb" style={{height:4}}><div className="pb-f" style={{width:`${p.pct}%`,background:`linear-gradient(90deg,${col},${col}99)`,borderRadius:2}}/></div>
                </div>
                <div style={{fontSize:12,fontWeight:660,color:col,flexShrink:0}}>{p.taps.toLocaleString()}</div>
              </div>
            );
          }) : <div style={{padding:'8px 20px',textAlign:'center',color:'var(--t3)',fontSize:12}}>No tap data</div>}
        </div>

        {/* Traffic Sources */}
        <div className="card fi s3">
          <div className="ch"><div><div className="ct">Traffic Sources</div><div className="cs">Leads by channel</div></div></div>
          {traffic?.sources?.length>0 ? (
            <>
              <div style={{display:"flex",justifyContent:"center",padding:"8px 0 6px"}}>
                <svg width="120" height="120" viewBox="0 0 120 120">
                  {(()=>{const r=44,cx=60,cy=60,circ=2*Math.PI*r;let off=0;
                    return traffic.sources.map((s,i)=>{const c=TRAFFIC_COL[i%TRAFFIC_COL.length];const dash=(s.pct/100)*circ,gap=circ-dash;
                      const el=<circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke={c} strokeWidth="18" strokeDasharray={`${dash} ${gap}`} strokeDashoffset={-off} transform={`rotate(-90 ${cx} ${cy})`}/>;
                      off+=dash;return el;});
                  })()}
                  <text x="60" y="55" textAnchor="middle" style={{fontSize:17,fontWeight:800,fill:"#0f172a"}}>{traffic.sources.reduce((s,x)=>s+x.count,0).toLocaleString()}</text>
                  <text x="60" y="70" textAnchor="middle" style={{fontSize:9,fill:"#94a3b8"}}>total leads</text>
                </svg>
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:8,padding:"0 20px 16px"}}>
                {traffic.sources.map((s,i)=>{const col=TRAFFIC_COL[i%TRAFFIC_COL.length];return (
                  <div key={s.label} style={{display:"flex",alignItems:"center",gap:9}}>
                    <div style={{width:10,height:10,borderRadius:"50%",background:col,flexShrink:0,boxShadow:`0 0 5px ${col}55`}}/>
                    <div style={{flex:1,fontSize:12,color:"var(--t2)",fontWeight:530}}>{s.label}</div>
                    <div style={{fontSize:11.5,color:"var(--t3)",marginRight:4}}>{s.count.toLocaleString()}</div>
                    <div style={{fontSize:12,fontWeight:680,color:col,minWidth:32,textAlign:"right"}}>{s.pct}%</div>
                  </div>
                );})}
              </div>
            </>
          ) : <div style={{padding:40,textAlign:'center',color:'var(--t3)',fontSize:12}}>No traffic data</div>}
        </div>
      </div>

      {/* ══════════ ROW 5: CONVERSION FUNNEL + CAMPAIGN PERFORMANCE ══════════ */}
      <div className="g2" style={{marginBottom:20}}>
        {/* Conversion Funnel */}
        <div className="card fi s1">
          <div className="ch">
            <div><div className="ct">Conversion Funnel</div><div className="cs">Lead pipeline stages</div></div>
            <span className="ai-chip">✦ AI-tracked</span>
          </div>
          <div className="cb">
            {funnel&&funnel.length>0 ? funnel.map((s,i)=>{
              const col=FUNNEL_COL[i%FUNNEL_COL.length];
              const nextCount = funnel[i+1]?.count;
              const dropOff = s.count>0 && nextCount!=null ? Math.round((1 - nextCount/s.count)*100) : null;
              return (
                <div key={s.stage} style={{marginBottom:i<funnel.length-1?14:0}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <div style={{width:28,height:28,borderRadius:8,background:`${col}14`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,flexShrink:0}}>{s.ico}</div>
                      <span style={{fontSize:12.5,fontWeight:570,color:"var(--t1)"}}>{s.stage}</span>
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <span style={{fontSize:12.5,fontWeight:680,color:col}}>{s.count.toLocaleString()}</span>
                      <span style={{fontSize:10,color:"var(--t3)",fontWeight:540}}>{s.pct}%</span>
                    </div>
                  </div>
                  <div className="pb" style={{height:5}}><div className="pb-f" style={{width:`${s.pct}%`,background:`linear-gradient(90deg,${col},${col}88)`,borderRadius:3}}/></div>
                  {dropOff!=null&&dropOff>0&&(
                    <div style={{display:"flex",justifyContent:"center",margin:"6px 0 0"}}>
                      <span style={{fontSize:9,color:"var(--t4)",fontWeight:600}}>▼ {dropOff}% drop-off</span>
                    </div>
                  )}
                </div>
              );
            }) : <div style={{padding:20,textAlign:'center',color:'var(--t3)',fontSize:12}}>No funnel data</div>}
          </div>
        </div>

        {/* Campaign Performance */}
        <div className="card fi s2">
          <div className="ch">
            <div><div className="ct">Campaign Performance</div><div className="cs">Active campaigns overview</div></div>
            <div className="ca" onClick={()=>navigate('/campaigns/email')}>View all</div>
          </div>
          <div style={{padding:"14px 20px 8px",display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            {[
              {lbl:"Active Campaigns",val:String(campPerf?.activeCampaigns??0),ico:"📡",col:"#0891b2",bg:"rgba(8,145,178,.1)",sh:"rgba(8,145,178,.2)"},
              {lbl:"Avg Open Rate",val:`${campPerf?.avgOpenRate??0}%`,ico:"📬",col:"#059669",bg:"rgba(5,150,105,.1)",sh:"rgba(5,150,105,.2)"},
              {lbl:"Total Sends",val:(campPerf?.totalSends??0).toLocaleString(),ico:"✉️",col:"#d97706",bg:"rgba(217,119,6,.1)",sh:"rgba(217,119,6,.2)"},
              {lbl:"Campaigns",val:String(campPerf?.campaigns?.length??0),ico:"📊",col:"#7c3aed",bg:"rgba(124,58,237,.1)",sh:"rgba(124,58,237,.2)"},
            ].map(({lbl,val,ico,col,bg,sh})=>(
              <div key={lbl} style={{...kpiCard,padding:"12px 14px",position:"relative",overflow:"hidden"}}>
                <div style={{position:"absolute",top:-16,right:-16,width:60,height:60,borderRadius:"50%",background:bg,opacity:.6,pointerEvents:"none"}}/>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                  <div style={{width:28,height:28,borderRadius:8,background:bg,boxShadow:`0 3px 10px ${sh}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13}}>{ico}</div>
                  <span style={{fontSize:10.5,color:"var(--t3)",fontWeight:540}}>{lbl}</span>
                </div>
                <div style={{fontSize:20,fontWeight:730,letterSpacing:"-.6px",color:col,lineHeight:1}}>{val}</div>
              </div>
            ))}
          </div>
          {campPerf?.campaigns?.length>0&&(
            <div style={{padding:"0 20px 16px"}}>
              {campPerf.campaigns.filter(c=>c.status==='active').slice(0,3).map(c=>(
                <div key={c.id} className="lr">
                  <div style={{width:32,height:32,borderRadius:9,background:"rgba(8,145,178,.1)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,flexShrink:0}}>✉️</div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:12.5,fontWeight:560,color:"var(--t1)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.name||c.subject}</div>
                    <div style={{fontSize:11,color:"var(--t3)",marginTop:1}}>{(c.total_send||0).toLocaleString()} sent · {c.reads_count||0} opens</div>
                  </div>
                  <Bdg s={c.status}/>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ══════════ ROW 6: MONTHLY GOALS / FOLLOW-UPS (from leads) / NFC STATUS ══════════ */}
      <div className="g3" style={{marginBottom:20}}>
        {/* Monthly Goals */}
        <div className="card fi s1">
          <div className="ch">
            <div><div className="ct">Monthly Goals</div><div className="cs">{goals?.month??'This month'} targets</div></div>
            <span style={{fontSize:10,fontWeight:640,color:"#059669",background:"rgba(5,150,105,.1)",padding:"3px 8px",borderRadius:20}}>📊 Progress</span>
          </div>
          <div className="cb">
            {(()=>{
              const items = [
                {lbl:"Leads Captured",current:goals?.leadsThisMonth??0,target:Math.max(goals?.totalLeads||100,100),col:"#0891b2",ico:"🎯"},
                {lbl:"NFC Taps",current:goals?.tapsThisMonth??0,target:Math.max(Math.round((goals?.tapsThisMonth||0)*1.3)||1000,1000),col:"#18181b",ico:"💳"},
                {lbl:"New Members",current:goals?.newMembersThisMonth??0,target:Math.max(goals?.totalMembers||20,20),col:"#d97706",ico:"👥"},
              ];
              return items.map(({lbl,current,target,col,ico})=>{
                const pct=Math.min(Math.round((current/target)*100),100);
                return (
                  <div key={lbl} style={{marginBottom:14}}>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
                      <div style={{display:"flex",alignItems:"center",gap:7}}>
                        <span style={{fontSize:13}}>{ico}</span>
                        <span style={{fontSize:12,fontWeight:560,color:"var(--t1)"}}>{lbl}</span>
                      </div>
                      <div style={{display:"flex",alignItems:"center",gap:6,fontSize:11,color:"var(--t3)",fontWeight:540}}>
                        <span style={{color:col,fontWeight:680}}>{current.toLocaleString()}</span> / {target.toLocaleString()}
                        <span style={{fontSize:9.5,fontWeight:700,color:pct>=80?"#059669":pct>=50?"#d97706":"#dc2626"}}>{pct}%</span>
                      </div>
                    </div>
                    <div className="pb" style={{height:6}}><div className="pb-f" style={{width:`${pct}%`,background:`linear-gradient(90deg,${col},${col}aa)`,borderRadius:3}}/></div>
                  </div>
                );
              });
            })()}
          </div>
        </div>

        {/* Recent Hot Leads (replaces Follow-ups since no follow-up table exists) */}
        <div className="card fi s2">
          <div className="ch">
            <div><div className="ct">Hot Leads</div><div className="cs">Highest scoring leads</div></div>
            <span style={{fontSize:10,fontWeight:640,color:"#dc2626",background:"rgba(220,38,38,.1)",padding:"3px 8px",borderRadius:20}}>🔥 Priority</span>
          </div>
          {leadQueue&&leadQueue.length>0 ? leadQueue.slice(0,5).map(l=>{
            const typeIco = {1:'💳',2:'📋',3:'✏️',4:'🤝'};
            const typeLbl = {1:'NFC Tap',2:'Form',3:'Manual',4:'Connected'};
            return (
              <div key={l.id} className="lr">
                <div style={{width:32,height:32,borderRadius:9,background:`${l.score>=80?'rgba(220,38,38,.1)':l.score>=50?'rgba(217,119,6,.1)':'rgba(100,116,139,.1)'}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,flexShrink:0}}>
                  {typeIco[l.type]||'🔗'}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <span style={{fontSize:12.5,fontWeight:560,color:"var(--t1)",overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{l.name||'Unknown'}</span>
                    {l.score>=80&&<span style={{width:6,height:6,borderRadius:"50%",background:"#dc2626",flexShrink:0,boxShadow:"0 0 5px rgba(220,38,38,.4)"}}/>}
                  </div>
                  <div style={{fontSize:11,color:"var(--t3)",marginTop:1}}>{l.company||'—'} · {typeLbl[l.type]||'Lead'}</div>
                </div>
                <AiScoreRing score={l.score??0}/>
              </div>
            );
          }) : <div className="lr" style={{justifyContent:'center',color:'var(--t3)',fontSize:12}}>No leads yet</div>}
        </div>

        {/* NFC Card Status */}
        <div className="card fi s3">
          <div className="ch">
            <div><div className="ct">NFC Card Status</div><div className="cs">Team card overview</div></div>
          </div>
          <div style={{display:"flex",justifyContent:"center",padding:"14px 0 10px"}}>
            <svg width="120" height="120" viewBox="0 0 120 120">
              {(()=>{
                const total=nfcStatus?.total||1;
                const src=[
                  {v:Math.round(((nfcStatus?.active??0)/total)*100),c:"#059669"},
                  {v:Math.round(((nfcStatus?.pending??0)/total)*100),c:"#d97706"},
                  {v:Math.round(((nfcStatus?.inactive??0)/total)*100),c:"#dc2626"},
                ];
                const r=44,cx=60,cy=60,circ=2*Math.PI*r;let off=0;
                return src.map(({v,c},i)=>{
                  if(v<=0) return null;
                  const dash=(v/100)*circ,gap=circ-dash;
                  const el=<circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke={c} strokeWidth="18" strokeDasharray={`${dash} ${gap}`} strokeDashoffset={-off} transform={`rotate(-90 ${cx} ${cy})`}/>;
                  off+=dash; return el;
                });
              })()}
              <text x="60" y="55" textAnchor="middle" style={{fontSize:17,fontWeight:800,fill:"#0f172a"}}>{nfcStatus?.total??0}</text>
              <text x="60" y="70" textAnchor="middle" style={{fontSize:9,fill:"#94a3b8"}}>total cards</text>
            </svg>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:6,padding:"0 20px 16px"}}>
            {[
              {lbl:"Active",count:nfcStatus?.active??0,col:"#059669",ico:"✅"},
              {lbl:"Pending",count:nfcStatus?.pending??0,col:"#d97706",ico:"⏳"},
              {lbl:"Inactive / Lost",count:nfcStatus?.inactive??0,col:"#dc2626",ico:"⚠️"},
            ].map(({lbl,count,col,ico})=>{
              const total=nfcStatus?.total||1;
              const pct=Math.round((count/total)*100);
              return (
                <div key={lbl} style={{display:"flex",alignItems:"center",gap:9}}>
                  <div style={{width:28,height:28,borderRadius:8,background:`${col}14`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,flexShrink:0}}>{ico}</div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:3}}>
                      <span style={{fontSize:12,fontWeight:540,color:"var(--t2)"}}>{lbl}</span>
                      <span style={{fontSize:11.5,fontWeight:680,color:col}}>{count}</span>
                    </div>
                    <div className="pb" style={{height:4}}><div className="pb-f" style={{width:`${pct}%`,background:col,borderRadius:2}}/></div>
                  </div>
                </div>
              );
            })}
          </div>
          {nfcStatus?.types?.length>0&&(
            <div style={{padding:"0 20px 16px"}}>
              {nfcStatus.types.map(t=>(
                <div key={t.type||'unknown'} style={{display:"flex",alignItems:"center",gap:9,padding:"5px 0",borderBottom:"1px solid rgba(0,0,0,.04)"}}>
                  <div style={{width:10,height:10,borderRadius:3,background:"#18181b",flexShrink:0,boxShadow:"0 0 6px rgba(24,24,27,.3)"}}/>
                  <span style={{flex:1,fontSize:12,color:"var(--t2)",fontWeight:530}}>{t.type||'NFC Card'}</span>
                  <span style={{fontSize:12,fontWeight:660,color:"#18181b"}}>{t.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ══════════ ROW 7: RECENT LEADS TABLE + ACTIVITY HEATMAP ══════════ */}
      <div className="g2e" style={{marginBottom:20}}>
        {/* Recent Leads Table */}
        <div className="card fi s1">
          <div className="ch">
            <div><div className="ct">Recent Leads</div><div className="cs">Latest captured contacts</div></div>
            <div className="ca" onClick={()=>navigate('/leads')}>All leads</div>
          </div>
          <div style={{overflowX:"auto"}}>
            <table className="tbl">
              <thead><tr><th>Name</th><th>Company</th><th>Source</th><th>Status</th><th>Score</th></tr></thead>
              <tbody>
                {recentLeads&&recentLeads.length>0 ? recentLeads.map(l=>(
                  <tr key={l.id}>
                    <td style={{fontWeight:560}}>{l.name||'—'}</td>
                    <td>{l.company||'—'}</td>
                    <td>
                      <span style={{display:"inline-flex",alignItems:"center",gap:5}}>
                        <span style={{fontSize:11}}>{l.source==='NFC Tap'?'💳':l.source==='Form'?'📋':'🔗'}</span>
                        {l.source}
                      </span>
                    </td>
                    <td><Bdg s={l.status||'new'}/></td>
                    <td><AiScoreRing score={l.score??0}/></td>
                  </tr>
                )) : <tr><td colSpan="5" style={{textAlign:'center',color:'var(--t3)'}}>No leads yet</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        {/* Lead Heatmap */}
        <div className="card fi s2">
          <div className="ch">
            <div><div className="ct">Lead Heatmap</div><div className="cs">Lead activity · hourly distribution</div></div>
            <span style={{fontSize:10,fontWeight:640,color:"#0891b2",background:"rgba(8,145,178,.1)",padding:"3px 8px",borderRadius:20}}>Last week</span>
          </div>
          <div className="cb">
            {(()=>{
              const matrix = heatmap?.matrix || [[0,0,0,0,0,0,0],[0,0,0,0,0,0,0],[0,0,0,0,0,0,0],[0,0,0,0,0,0,0],[0,0,0,0,0,0,0],[0,0,0,0,0,0,0]];
              const hours = heatmap?.hourLabels || ['6a','9a','12p','3p','6p','9p'];
              const days = heatmap?.dayNames || ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
              const maxV = Math.max(...matrix.flat(),1);
              return (
                <>
                  <div style={{display:"grid",gridTemplateColumns:"32px repeat(7,1fr)",gap:3}}>
                    <div/>
                    {days.map(d=><div key={d} style={{fontSize:9,fontWeight:700,color:"var(--t4)",textAlign:"center",letterSpacing:".04em"}}>{d}</div>)}
                    {hours.map((hour,hi)=>[
                      <div key={`lbl-${hour}`} style={{fontSize:9,fontWeight:600,color:"var(--t4)",display:"flex",alignItems:"center"}}>{hour}</div>,
                      ...matrix[hi].map((v,di)=>{
                        const opacity=v===0?0:Math.max(0.08,(v/maxV)*0.9);
                        return (
                          <div key={`${hour}-${di}`} style={{
                            aspectRatio:"1",borderRadius:4,
                            background:v===0?"rgba(0,0,0,.03)":`rgba(8,145,178,${opacity})`,
                            display:"flex",alignItems:"center",justifyContent:"center",
                            fontSize:8,fontWeight:600,color:opacity>.5?"#fff":"transparent",cursor:"default",
                          }} title={`${days[di]} ${hour}: ${v}`}>{v>0?v:""}</div>
                        );
                      }),
                    ]).flat()}
                  </div>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"flex-end",gap:5,marginTop:10}}>
                    <span style={{fontSize:9,color:"var(--t4)",fontWeight:600}}>Less</span>
                    {[0,.15,.35,.6,.9].map((o,i)=>(
                      <div key={i} style={{width:12,height:12,borderRadius:3,background:o===0?"rgba(0,0,0,.03)":`rgba(8,145,178,${o})`}}/>
                    ))}
                    <span style={{fontSize:9,color:"var(--t4)",fontWeight:600}}>More</span>
                  </div>
                  {heatmap?.peak?.count>0&&(
                    <div style={{display:"flex",alignItems:"center",gap:12,marginTop:14,padding:"10px 12px",background:"rgba(8,145,178,.05)",borderRadius:10,border:"1px solid rgba(8,145,178,.1)"}}>
                      <span style={{fontSize:14}}>💡</span>
                      <div>
                        <div style={{fontSize:11.5,fontWeight:600,color:"var(--t1)"}}>Peak: {heatmap.peak.day} {heatmap.peak.hour}</div>
                        <div style={{fontSize:10.5,color:"var(--t3)",marginTop:1}}>Schedule campaigns in this window for best engagement</div>
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      </div>

      {/* ══════════ ROW 8: CAMPAIGN QUICK STATS WITH SPARKLINES ══════════ */}
      <div className="g4">
        {[
          {lbl:"Avg Open Rate",val:campPerf?.avgOpenRate??0,suf:"%",col:"#0891b2",bg:"rgba(8,145,178,.1)",sh:"rgba(8,145,178,.2)",ico:"📬",spark:[24,26,28,27,30,31,campPerf?.avgOpenRate??32]},
          {lbl:"Active Campaigns",val:campPerf?.activeCampaigns??0,suf:"",col:"#059669",bg:"rgba(5,150,105,.1)",sh:"rgba(5,150,105,.2)",ico:"📡",spark:[1,2,2,3,2,3,campPerf?.activeCampaigns??3]},
          {lbl:"Total Sends",val:campPerf?.totalSends??0,suf:"",col:"#18181b",bg:"rgba(15,15,30,.08)",sh:"rgba(15,15,30,.16)",ico:"✉️",spark:[100,200,400,800,1200,1800,campPerf?.totalSends??2640]},
          {lbl:"Total Leads",val:stats?.totalLeads??0,suf:"",col:"#d97706",bg:"rgba(217,119,6,.1)",sh:"rgba(217,119,6,.2)",ico:"🎯",spark:SPARKS.leads},
        ].map(({lbl,val,suf,col,bg,sh,ico,spark},i)=>(
          <div key={lbl} className={`fi s${i+1}`} style={{...kpiCard,padding:"18px 20px",position:"relative",overflow:"hidden",minWidth:0}}>
            <div style={{position:"absolute",top:-20,right:-20,width:90,height:90,borderRadius:"50%",background:bg,opacity:.6,pointerEvents:"none"}}/>
            <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:10}}>
              <div style={{width:36,height:36,borderRadius:10,background:bg,boxShadow:`0 4px 14px ${sh}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16}}>{ico}</div>
              <Sparkline data={spark} color={col} height={38} width={80}/>
            </div>
            <div style={{fontSize:11,color:"var(--t3)",marginBottom:5,fontWeight:540}}>{lbl}</div>
            <div style={{fontFamily:"var(--fd)",fontSize:28,color:col,letterSpacing:"-1px",lineHeight:1}}><Counter to={val}/>{suf}</div>
          </div>
        ))}
      </div>
    </div>
  );
});
